import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Container,
  Grid,
  IconButton,
  Popover,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Typography,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  Tooltip,
  Autocomplete,
} from "@mui/material";
import {
  exportToExcel,
  extractTemplateVars,
  getHeader,
  getReplyVars,
  getTemplateColumns,
  getVariableNames,
  toastError,
  validNoSpecialChar,
} from "../../utils/util";
import TemplatePreview from "../../widgets/TemplatePreview";
import BackBtn from "../../widgets/BackBtn";
import axios from "axios";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { API_ENDPOINT, MEDIA_TYPES } from "../../utils/constants";
import { toast } from "react-toastify";
import { LoadingButton } from "@mui/lab";
import ImportExportIcon from "@mui/icons-material/ImportExport";
import InfoIcon from "@mui/icons-material/Info";
import PermPhoneMsgIcon from "@mui/icons-material/PermPhoneMsg";
import MyDatePicker from "../../widgets/MyDatePicker";
import MyTimePicker from "../../widgets/MyTimePicker";
import { DownloadDoneOutlined } from "@mui/icons-material";
import useApiEp from "../../hooks/useApiEp";
import MediaSelector from "./headers/MediaSelector";
import LocationForm from "./headers/LocationForm";
import BlockHolder from "../../widgets/BlockHolder";
import CustomTabs from "../../widgets/CustomTabs";
import SectionLabel from "../../widgets/SectionLabel";
import ContactTableDynamic from "./ContactTable";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
const HeaderLabel = ({ children, ...props }) => {
  return (
    <Typography fontSize="16px" fontWeight={500} mb={2} {...props}>
      {children}
    </Typography>
  );
};
/*

header [file, param, location]
bodyparam: []
contact list

*/

const MainCampaign = () => {
  const { id } = useParams();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedQuickReply, setSelectedQuickReply] = useState(null); // { type, text }
  const [responseText, setResponseText] = useState("");
  const [updating, setUpdating] = useState(false);
  const [templateHeader, setTemplateHeader] = useState();
  /* Emoji-picker anchor */
  const [emojiAnchor, setEmojiAnchor] = useState(null);
  const pickerOpen = Boolean(emojiAnchor);
  const [sending, setSending] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [excelData, setExcelData] = useState([]);
  const [excelLoading, setExcelLoading] = useState(false);
  const [campaignName, setCampaignName] = useState("");
  const [campaignNameError, setCampaignNameError] = useState("");
  const [selectedGroup, setSelectedGroup] = useState("");
  const [contactGroup, setContactGroup] = useState(0);
  const [templateButtons, setTemplateButtons] = useState();
  const [bodyParams, setBodyParams] = useState();
  const [templateBody, setTemplateBody] = useState();
  const [componentButtons, setComponentButtons] = useState([]);
  const [isCampaignScheduled, setIsCampaignScheduled] = useState(false);
  const [configureAutoResponse, setConfigureAutoResponse] = useState(false);
  const [campaignSchedule, setCampaignSchedule] = useState(new Date());
  const [template, setTemplate] = useState();
  const [updatedVars, setUpdatedVars] = useState({});
  const [headerVar, setHeaderVar] = useState("");
  const [headerVarError, setHeaderVarError] = useState("");
  const [mediaUrl, setMediaUrl] = useState("");
  const [mediaId, setMediaId] = useState("");
  const [mediaName, setMediaName] = useState("");
  const [locationForm, setLocationForm] = useState({
    latitude: "",
    longitude: "",
    name: "",
    address: "",
  });
  const open = Boolean(anchorEl);

  const {
    data: templateD,
    isLoading,
    isError,
    error,
    refetch,
  } = useApiEp(`/api/get-template/${id}`);
  const { data: contactList, isLoading: loadingList } =
    useApiEp(`/user/group-list`);

  const headerType = useMemo(() => {
    if (templateD) {
      return templateD.components.find((item) => item.type === "HEADER")
        ?.format;
    }
  }, [templateD]);
  const headerVarExists = useMemo(() => {
    if (headerType === "TEXT") {
      const headerText = templateD.components.find(
        (item) => item.type === "HEADER"
      ).text;
      return getVariableNames("POSITIONAL", headerText).length > 0;
    }
  }, [headerType]);

  const replyVars = useMemo(() => {
    if (templateD) {
      return getReplyVars(templateD);
    }
  }, [templateD]);

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" my={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box m={2}>
        <Alert severity="error">
          {error?.message || "Failed to load template"}
        </Alert>
      </Box>
    );
  }
  const handleTemplateDownload = () => {
    if (!templateD) return;
    const columnHeaders = ["Numbers"].concat(getTemplateColumns(templateD));
    exportToExcel([columnHeaders], `template_${templateD.name}.xlsx`, true);
  };

  const handlePopoverOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) {
      setExcelLoading(false);
      return;
    }
    // Validate file type
    if (!file.name.match(/\.(xlsx|xls|csv)$/)) {
      toast.warn("Please upload a valid Excel file");
      setExcelLoading(false);
      return;
    }

    // Validate file size
    const MAX_FILE_SIZE = 50 * 1024 * 1024; // 5MB
    if (file.size > MAX_FILE_SIZE) {
      toast.warn("File size exceeds 50MB limit");
      setExcelLoading(false);
      return;
    }

    setExcelLoading(true);
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const arrayBuffer = e.target.result;
        const workbook = XLSX.read(arrayBuffer, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(sheet, {
          header: 1,
          defval: "",
        });
        if (jsonData.length === 0) {
          toast.warn("The uploaded file is empty");
          setExcelData([]);
          return;
        }

        const headers = jsonData[0];
        if (!headers || headers.length === 0) {
          toast.warn("No headers found in the file");
          setExcelData([]);
          return;
        }

        const formattedData = jsonData.slice(1).map((row) => {
          const obj = {};
          headers.forEach((header, index) => {
            obj[header] = row[index] ?? "";
          });
          return obj;
        });

        setExcelData(formattedData);
      } catch (err) {
        toast.error("Error reading file");
        console.error("File read error:", err);
      } finally {
        setExcelLoading(false);
      }
    };
    reader.onerror = () => {
      toast.error("Failed to read file");
      setExcelLoading(false);
    };
    reader.readAsArrayBuffer(file);
  };

  const validateHeader = () => {
    if (!headerType) return true;

    if (headerVarExists && !headerVar) {
      toast.error("Please enter header variable value!");
      return false;
    }
    if (headerType && MEDIA_TYPES.includes(headerType) && !mediaUrl) {
      toast.error("Please select a media url for header");
      return false;
    }
    if (
      headerType === "LOCATION" &&
      !(locationForm.latitude && locationForm.longitude)
    ) {
      toast.error("Please enter location details");
      return false;
    }
    return true; // ✅ all good
  };
  function getPayloadHeader(ht) {
    if (ht === "TEXT") {
      const hd = { type: "header" };
      if (headerVarExists) {
        hd.parameters = [{ type: "text", text: headerVar }];
      }
      return hd;
    } else if (MEDIA_TYPES.includes(ht)) {
      const type = ht.toLowerCase();
      return {
        type: "header",
        parameters: [
          {
            type,
            [type]: {
              id: mediaId,
              ...(ht === "DOCUMENT" && { filename: mediaName }),
            },
          },
        ],
      };
    } else {
      return {
        type: "header",
        parameters: [
          {
            type: "location",
            location: locationForm,
          },
        ],
      };
    }
  }
  const handleSend = async () => {
    if (!campaignName.trim()) {
      toast.error("Please enter a campaign name");
      return;
    }
    if (excelData.length === 0) {
      toast.error("Please upload contact data");
      return;
    }

    if (!validateHeader()) {
      return;
    }
    const payload = {
      campaignName,
      contacts: excelData,
      templateName: id,
      templateLanguage: templateD.language,
    };
    if (headerType) {
      payload.header = getPayloadHeader(headerType);
    }
    const buttonsComponent = templateD.components.find(
      (item) => item.type === "BUTTONS"
    );
    const flowButton = buttonsComponent?.buttons?.find(
      (btn) => btn.type === "FLOW"
    );
    if (flowButton) {
      payload.buttons = [
        {
          type: "button",
          sub_type: "flow",
          index: 0,
          parameters: [
            {
              type: "action",
              action: {
                flow_action_data: {
                  screen: flowButton.navigate_screen,
                },
              },
            },
          ],
        },
      ];
    }

    try {
      setSending(true);
      const response = await axios.post(
        `${API_ENDPOINT}/api/main-campaign`,
        payload,
        getHeader()
      );
      toast.success(response.data.message);
      setExcelData([]); // Clear data after successful send
      setCampaignName("");
    } catch (error) {
      toastError(error);
    } finally {
      setSending(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <BackBtn />
      <Grid container spacing={3}>
        <Grid item xs={12} md={7}>
          <Box>
            <Typography variant="h6" fontWeight="500" gutterBottom>
              Campaign
            </Typography>
            <BlockHolder>
              <FormControlLabel
                control={
                  <Checkbox
                    value={isCampaignScheduled}
                    onChange={(e) => setIsCampaignScheduled(e.target.checked)}
                  />
                }
                label="Is campaign scheduled?"
                sx={{ mb: 1 }}
              />
              {isCampaignScheduled && (
                <Stack direction={"row"} gap={2}>
                  <MyDatePicker />
                  <MyTimePicker />
                </Stack>
              )}
            </BlockHolder>

            <Box>
              {headerType === "TEXT" && headerVarExists && (
                <BlockHolder>
                  <HeaderLabel>Header variable</HeaderLabel>
                  <TextField
                    sx={{ my: 1 }}
                    fullWidth
                    label="Header var text value"
                    size="small"
                    variant="outlined"
                    value={headerVar}
                    onChange={(e) => {
                      validNoSpecialChar({
                        label: "Header var text value",
                        value: e.target.value,
                        setValue: setHeaderVar,
                        setError: setHeaderVarError,
                        maxLength: 50,
                      });
                    }}
                    error={!!headerVarError}
                    helperText={headerVarError}
                    inputProps={{ maxLength: 50 }}
                  />
                </BlockHolder>
              )}
              {headerType === "LOCATION" && (
                <Box mt={1}>
                  <Typography fontSize={"17px"} gutterBottom>
                    Location details
                  </Typography>
                  <LocationForm
                    onChange={(newV) => {
                      setLocationForm(newV);
                    }}
                  />
                </Box>
              )}
              {headerType && MEDIA_TYPES.includes(headerType) && (
                <Box mt={1}>
                  <HeaderLabel>Media Header</HeaderLabel>
                  <MediaSelector
                    value={templateHeader}
                    onChange={(newV) => {
                      setMediaUrl(newV.url);
                      setMediaId(newV.mediaId);
                      setMediaName(newV.fileName);
                    }}
                    type={headerType}
                  />
                </Box>
              )}

              {replyVars && replyVars.length > 0 && (
                <Box sx={{ my: 1 }}>
                  <BlockHolder>
                    <Typography fontSize={"15px"}>
                      There are {replyVars.length} reply buttons. Please ensure
                      you have configured auto replies{" "}
                      <a href="/home/auto-replies" target="_blank">
                        here
                      </a>
                    </Typography>
                  </BlockHolder>
                </Box>
              )}
            </Box>

            <TextField
              fullWidth
              label="Campaign Name"
              value={campaignName}
              onChange={(e) => {
                validNoSpecialChar({
                  label: "CampaignName",
                  value: e.target.value,
                  setValue: setCampaignName,
                  setError: setCampaignNameError,
                  maxLength: 30,
                });
              }}
              margin="normal"
              variant="outlined"
              size="small"
              error={!!campaignNameError}
              helperText={campaignNameError}
              inputProps={{
                maxLength: 30, // Enforce max length at the input level
              }}
            />

            <SectionLabel sx={{ mt: 2 }}>Target</SectionLabel>
            <CustomTabs
              tabs={[
                {
                  label: "Excel Import",
                  icon: (
                    <ImportExportIcon
                      fontSize="small"
                      sx={{ transition: "all 0.3s" }}
                    />
                  ),
                },
                {
                  label: "Contact Group",
                  icon: (
                    <PermPhoneMsgIcon
                      fontSize="small"
                      sx={{ transition: "all 0.3s" }}
                    />
                  ),
                },
              ]}
              value={contactGroup}
              onChange={(e, newValue) => {
                setContactGroup(newValue);
              }}
              containerSx={{ width: "100%", my: 1 }}
            />
            {contactGroup === 0 && (
              <Box>
                <Stack direction="row" alignItems="center" mb={2}>
                  <Typography variant="body2">
                    Please read these instructions before importing
                  </Typography>
                  <IconButton
                    onClick={handlePopoverOpen}
                    size="small"
                    sx={{ ml: 1 }}
                  >
                    <InfoIcon />
                  </IconButton>
                </Stack>
                <Popover
                  open={open}
                  anchorEl={anchorEl}
                  onClose={handlePopoverClose}
                  anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                  transformOrigin={{ vertical: "top", horizontal: "left" }}
                  PaperProps={{
                    sx: {
                      borderRadius: 2,
                      boxShadow: 3,
                      border: "1px solid",
                      borderColor: "grey.200",
                    },
                  }}
                >
                  <Box sx={{ p: 2, maxWidth: 320 }}>
                    <Typography
                      variant="subtitle2"
                      color="primary"
                      fontWeight="600"
                      mb={1}
                    >
                      File Requirements
                    </Typography>
                    <Box component="ul" sx={{ pl: 2, m: 0, "& li": { mb: 1 } }}>
                      <Typography component="li" variant="body2">
                        The first column must be the whatsapp numbers
                      </Typography>
                      <Typography component="li" variant="body2">
                        After first column, your variable names(numbered eg.{" "}
                        {`{{1}}`} or named eg. {`{{'order_id'}}`}) must appear
                        in the same order in which they appear in the message
                        body.
                      </Typography>
                      <Typography component="li" variant="body2">
                        Atleast one line item should be there
                      </Typography>
                    </Box>
                    <Box sx={{ mt: 2, textAlign: "right" }}>
                      <Button size="small" onClick={handlePopoverClose}>
                        Got it
                      </Button>
                    </Box>
                  </Box>
                </Popover>
                <Stack direction={"row"} spacing={1}>
                  <Button
                    startIcon={<ImportExportIcon />}
                    variant="contained"
                    component="label"
                    disabled={excelLoading}
                    sx={{ mb: 3 }}
                  >
                    {excelLoading ? "Processing..." : "Import Excel File"}
                    <input
                      type="file"
                      accept=".xlsx,.xls"
                      onChange={handleFileUpload}
                      hidden
                    />
                  </Button>
                  <Button
                    onClick={handleTemplateDownload}
                    startIcon={<DownloadDoneOutlined />}
                    variant="outlined"
                  >
                    Downlod template
                  </Button>
                </Stack>
                {excelData.length > 10 && (
                  <Typography variant="caption">
                    First 10 items in the sheet
                  </Typography>
                )}
                {excelData.length > 0 && (
                  <TableContainer
                    component={Paper}
                    sx={{ mb: 3, maxHeight: 400 }}
                  >
                    <Table stickyHeader>
                      <TableHead>
                        <TableRow>
                          {Object.keys(excelData[0]).map((header) => (
                            <TableCell
                              key={header}
                              sx={{ bgcolor: "grey.100", fontWeight: "bold" }}
                            >
                              {header}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {excelData.slice(0, 10).map((row, rowIndex) => (
                          <TableRow key={rowIndex}>
                            {Object.keys(excelData[0]).map((header) => (
                              <TableCell key={`${rowIndex}-${header}`}>
                                {row[header]}
                              </TableCell>
                            ))}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
            )}
            {contactGroup === 1 && (
              <Box>
                <Autocomplete
                  size="small"
                  loading={loadingList}
                  sx={{ minWidth: 164, my: 2 }}
                  value={selectedGroup}
                  onChange={(newV) => {
                    setSelectedGroup(newV);
                  }}
                  options={contactList || []}
                  renderInput={(params) => (
                    <TextField {...params} label="Group Name" />
                  )}
                />
                <ContactTableDynamic
                  apiUrl={`${API_ENDPOINT}/user/get-contacts-for-campaign?group=Anniversary`}
                  onChange={(newV) => {
                    console.log(newV);
                  }}
                />
              </Box>
            )}

            <Box display={"flex"} justifyContent={"end"} mt={2}>
              <LoadingButton
                loading={sending}
                variant="contained"
                startIcon={<CampaignOutlinedIcon />}
                onClick={handleSend}
                disabled={excelLoading || !campaignName.trim() || isLoading}
                sx={{ mt: 2 }}
              >
                Start Campaign
              </LoadingButton>
            </Box>
          </Box>
        </Grid>

        <Grid item xs={12} md={5}>
          {templateD && (
            <TemplatePreview template={templateD} previewUrl={mediaUrl} />
          )}
        </Grid>
      </Grid>
    </Container>
  );
};

export default MainCampaign;
